import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { isVideo } from "../lib/site";
import { useContent } from "../lib/content";

const CYCLE_MS = 4000;

const ServiceMedia = ({ imgs, title, sid }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (imgs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), CYCLE_MS);
    return () => clearInterval(t);
  }, [imgs.length]);
  const src = imgs[idx % imgs.length];
  return (
    <div className="glow-card relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.4)] h-[300px] lg:h-[380px]" data-testid={`service-media-${sid}`}>
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          {isVideo(src) ? (
            <video src={src} muted loop autoPlay playsInline preload="metadata" className="w-full h-full object-cover" />
          ) : (
            <img src={src} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          )}
        </motion.div>
      </AnimatePresence>
      {imgs.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-10" data-testid={`service-media-dots-${sid}`}>
          {imgs.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show media ${i + 1}`}
              data-testid={`service-media-dot-${sid}-${i}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === idx % imgs.length ? "w-6 bg-[#ffc4e4]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const Services = () => {
  const { services } = useContent();
  if (!services || services.length === 0) return null;
  return (
    <section id="services" className="py-24 lg:py-28 bg-[#3c003a] relative grain overflow-hidden" data-testid="services-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 space-y-20">
        {services.map((s, i) => {
          const imgs = s.imgs || [];
          return (
            <div
              key={s.id}
              className={`grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
              data-testid={`service-item-${s.id}`}
            >
              <div className="lg:[direction:ltr]">
                <Reveal>
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#c9006f]/50 bg-white/5 backdrop-blur-md px-4 py-2 mb-7">
                    <Sparkles size={14} className="text-[#c9006f]" />
                    <span className="text-[#ffc4e4] text-xs tracking-[0.22em] uppercase font-semibold" data-testid={`service-badge-${s.id}`}>
                      {s.badge}
                    </span>
                  </span>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-6" data-testid={`service-title-${s.id}`}>
                    {s.title}
                  </h2>
                </Reveal>
                <Reveal delay={0.15}>
                  <p className="text-white/60 text-base md:text-lg leading-[1.7] max-w-xl" data-testid={`service-desc-${s.id}`}>
                    {s.desc}
                  </p>
                </Reveal>
              </div>
              {imgs.length > 0 ? (
                <Reveal delay={0.15} className="lg:[direction:ltr]">
                  <ServiceMedia imgs={imgs} title={s.title} sid={s.id} />
                </Reveal>
              ) : (
                <div className="hidden lg:block" />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
